var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from 'react';
import { merge } from 'lodash-es';
export function isNumber(obj) {
    return Object.prototype.toString.call(obj) === '[object Number]' && obj === obj;
}
const toNumber = (value, defaultValue) => {
    if (value === undefined) {
        return defaultValue;
    }
    if (isNumber(value)) {
        return value;
    }
    const numberVal = parseFloat(value);
    return isNumber(numberVal) ? numberVal : defaultValue;
};
const defaultOptions = {
    rotate: -20,
    zIndex: 1,
    width: 100,
    gap: [100, 100],
    fontStyle: {
        fontSize: '16px',
        color: 'rgba(0, 0, 0, 0.15)',
        fontFamily: 'sans-serif',
        fontWeight: 'normal',
    },
    getContainer: () => document.body,
};
const getMergedOptions = (o) => {
    var _a, _b, _c, _d, _e, _f;
    const options = o || {};
    const mergedOptions = Object.assign(Object.assign({}, options), { rotate: options.rotate || defaultOptions.rotate, zIndex: options.zIndex || defaultOptions.zIndex, fontStyle: Object.assign(Object.assign({}, defaultOptions.fontStyle), options.fontStyle), width: toNumber(options.width, options.image ? defaultOptions.width : undefined), height: toNumber(options.height, undefined), getContainer: options.getContainer, gap: [
            toNumber((_a = options.gap) === null || _a === void 0 ? void 0 : _a[0], defaultOptions.gap[0]),
            toNumber(((_b = options.gap) === null || _b === void 0 ? void 0 : _b[1]) || ((_c = options.gap) === null || _c === void 0 ? void 0 : _c[0]), defaultOptions.gap[1]),
        ] });
    const mergedOffsetX = toNumber((_d = mergedOptions.offset) === null || _d === void 0 ? void 0 : _d[0], 0);
    const mergedOffsetY = toNumber(((_e = mergedOptions.offset) === null || _e === void 0 ? void 0 : _e[1]) || ((_f = mergedOptions.offset) === null || _f === void 0 ? void 0 : _f[0]), 0);
    mergedOptions.offset = [mergedOffsetX, mergedOffsetY];
    return mergedOptions;
};
const measureTextSize = (ctx, content, rotate) => {
    let width = 0;
    let height = 0;
    const lineSize = [];
    content.forEach((item) => {
        const { width: textWidth, fontBoundingBoxAscent, fontBoundingBoxDescent, } = ctx.measureText(item);
        const textHeight = fontBoundingBoxAscent + fontBoundingBoxDescent;
        if (textWidth > width) {
            width = textWidth;
        }
        height += textHeight;
        lineSize.push({ height: textHeight, width: textWidth });
    });
    const angle = (rotate * Math.PI) / 180;
    return {
        originWidth: width,
        originHeight: height,
        width: Math.ceil(Math.abs(Math.sin(angle) * height) + Math.abs(Math.cos(angle) * width)),
        height: Math.ceil(Math.abs(Math.sin(angle) * width) + Math.abs(height * Math.cos(angle))),
        lineSize,
    };
};
const getCanvasData = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const { rotate, image, content, fontStyle, gap } = options;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio;
    const configCanvas = (size) => {
        const canvasWidth = gap[0] + size.width;
        const canvasHeight = gap[1] + size.height;
        canvas.setAttribute('width', `${canvasWidth * ratio}px`);
        canvas.setAttribute('height', `${canvasHeight * ratio}px`);
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.translate((canvasWidth * ratio) / 2, (canvasHeight * ratio) / 2);
        ctx.scale(ratio, ratio);
        const RotateAngle = (rotate * Math.PI) / 180;
        ctx.rotate(RotateAngle);
    };
    const drawText = () => {
        const { fontSize, color, fontWeight, fontFamily } = fontStyle;
        const realFontSize = toNumber(fontSize, 0) || fontStyle.fontSize;
        ctx.font = `${fontWeight} ${realFontSize}px ${fontFamily}`;
        const measureSize = measureTextSize(ctx, [...content], rotate);
        const width = options.width || measureSize.width;
        const height = options.height || measureSize.height;
        configCanvas({ width, height });
        ctx.fillStyle = color;
        ctx.font = `${fontWeight} ${realFontSize}px ${fontFamily}`;
        ctx.textBaseline = 'top';
        [...content].forEach((item, index) => {
            const { height: lineHeight, width: lineWidth } = measureSize.lineSize[index];
            const xStartPoint = -lineWidth / 2;
            const yStartPoint = -(options.height || measureSize.originHeight) / 2 + lineHeight * index;
            ctx.fillText(item, xStartPoint, yStartPoint, options.width || measureSize.originWidth);
        });
        return Promise.resolve({ base64Url: canvas.toDataURL(), height, width });
    };
    function drawImage() {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.referrerPolicy = 'no-referrer';
            img.src = image;
            img.onload = () => {
                let { width, height } = options;
                if (!width || !height) {
                    if (width) {
                        height = (img.height / img.width) * +width;
                    }
                    else {
                        width = (img.width / img.height) * +height;
                    }
                }
                configCanvas({ width, height });
                ctx.drawImage(img, -width / 2, -height / 2, width, height);
                return resolve({ base64Url: canvas.toDataURL(), width, height });
            };
            img.onerror = () => {
                return drawText();
            };
        });
    }
    return image ? drawImage() : drawText();
});
export default function useWatermark(params) {
    const [options, setOptions] = useState(params || {});
    const mergedOptions = getMergedOptions(options);
    const watermarkDiv = useRef();
    const mutationObserver = useRef();
    const container = mergedOptions.getContainer();
    const { zIndex, gap } = mergedOptions;
    function drawWatermark() {
        if (!container) {
            return;
        }
        getCanvasData(mergedOptions).then(({ base64Url, width, height }) => {
            var _a, _b;
            const offsetLeft = mergedOptions.offset[0] + 'px';
            const offsetTop = mergedOptions.offset[1] + 'px';
            const wmStyle = `
      width:calc(100% - ${offsetLeft});
      height:calc(100% - ${offsetTop});
      position:absolute;
      top:${offsetTop};
      left:${offsetLeft};
      bottom:0;
      right:0;
      pointer-events: none;
      z-index:${zIndex};
      background-position: 0 0;
      background-size:${gap[0] + width}px ${gap[1] + height}px;
      background-repeat: repeat;
      background-image:url(${base64Url})`;
            if (!watermarkDiv.current) {
                const div = document.createElement('div');
                watermarkDiv.current = div;
                container.append(div);
                container.style.position = 'relative';
            }
            (_a = watermarkDiv.current) === null || _a === void 0 ? void 0 : _a.setAttribute('style', wmStyle.trim());
            if (container) {
                (_b = mutationObserver.current) === null || _b === void 0 ? void 0 : _b.disconnect();
                mutationObserver.current = new MutationObserver((mutations) => {
                    var _a, _b;
                    const isChanged = mutations.some((mutation) => {
                        let flag = false;
                        if (mutation.removedNodes.length) {
                            flag = Array.from(mutation.removedNodes).some((node) => node === watermarkDiv.current);
                        }
                        if (mutation.type === 'attributes' && mutation.target === watermarkDiv.current) {
                            flag = true;
                        }
                        return flag;
                    });
                    if (isChanged) {
                        (_b = (_a = watermarkDiv.current) === null || _a === void 0 ? void 0 : _a.parentNode) === null || _b === void 0 ? void 0 : _b.removeChild(watermarkDiv.current);
                        watermarkDiv.current = undefined;
                        drawWatermark();
                    }
                });
                mutationObserver.current.observe(container, {
                    attributes: true,
                    subtree: true,
                    childList: true,
                });
            }
        });
    }
    useEffect(() => {
        drawWatermark();
    }, [options]);
    return {
        generateWatermark: (newOptions) => {
            setOptions(merge({}, options, newOptions));
        }
    };
}
